import { useState, useEffect } from 'react'
import { api } from '../lib/api'
import { useDatePreferences } from '../hooks/useDatePreferences'
import { useToast } from '../components/Toast'
import { useConfirm } from '../components/ConfirmModal'
import {
  Building2,
  Plus,
  Search,
  Filter,
  ChevronDown,
  Edit2,
  Trash2,
  X,
  Send,
  Upload,
  FileText,
  Sparkles,
  CheckCircle,
  Eye,
  Download,
  Shield,
  AlertTriangle,
  Play,
  Clock,
  RefreshCw,
  Globe,
  MoreVertical,
  Mail,
  Copy,
  Bell,
  ExternalLink,
  ChevronUp,
  ChevronLeft,
  ArrowUp,
  ArrowDown,
  Save,
  Loader2,
  BookOpen,
  HelpCircle,
  ArrowRight,
  BarChart3,
  Calendar,
  ClipboardList,
  Brain,
  Target,
} from 'lucide-react'

// ============================================================================
// Types
// ============================================================================

interface Vendor {
  id: string
  name: string
  description?: string
  category?: string
  risk_level: string
  status: string
  contact_name?: string
  contact_email?: string
  contact_phone?: string
  website?: string
  domains?: string[] | string // JSON string from backend or parsed array
  contract_start?: number
  contract_end?: number
  notes?: string
  questionnaire_count?: number
  last_assessment?: number
  created_at: number
}

interface Questionnaire {
  id: string
  vendor_id: string
  vendor_name?: string
  title: string
  description?: string
  status: string
  due_date?: number
  sent_at?: number
  completed_at?: number
  question_count?: number
  answered_count?: number
  created_at: number
  access_token?: string
  vendor_email?: string
  vendor_contact_name?: string
  link_status?: string
  link_sent_at?: number
  link_opened_at?: number
  link_views?: number
  reminder_count?: number
  access_expires_at?: number
}

interface IncomingQuestionnaire {
  id: string
  sender_org: string
  sender_contact?: string
  sender_email?: string
  original_file_name?: string
  title?: string
  status: string
  received_date?: number
  due_date?: number
  total_questions: number
  answered_questions: number
  created_at: number
}

interface IncomingQuestionnaireItem {
  id: string
  question_number: number
  category?: string
  question: string
  ai_answer?: string
  ai_confidence?: number
  ai_sources?: string
  final_answer?: string
  status: string
  reviewer_notes?: string
}

interface RiskAssessment {
  id: string
  vendor_id: string
  vendor_name?: string
  vendor_website?: string
  assessment_type: string
  trigger: string
  status: string
  started_at?: number
  completed_at?: number
  duration?: number
  overall_risk_score?: number
  overall_risk_level?: string
  security_score?: number
  reputation_score?: number
  compliance_score?: number
  dark_web_score?: number
  ai_summary?: string
  ai_recommendations?: string
  data_sources?: string
  raw_source_data?: string
  created_at: number
}

interface AssessmentQueueItem {
  id: string
  vendor_id: string
  vendor_name?: string
  vendor_website?: string
  vendor_risk_level?: string
  priority: number
  reason?: string
  scheduled_for?: number
  status: string
  created_at: number
}

// ============================================================================
// Config
// ============================================================================

const riskLevelConfig: Record<string, { label: string; color: string }> = {
  critical: { label: 'Critical', color: 'badge-red' },
  high: { label: 'High', color: 'badge-yellow' },
  medium: { label: 'Medium', color: 'badge-blue' },
  low: { label: 'Low', color: 'badge-green' },
}

const vendorStatusConfig: Record<string, { label: string; color: string }> = {
  active: { label: 'Active', color: 'badge-green' },
  inactive: { label: 'Inactive', color: 'badge-gray' },
  pending: { label: 'Pending Review', color: 'badge-yellow' },
  terminated: { label: 'Terminated', color: 'badge-red' },
}

const questionnaireStatusConfig: Record<string, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'badge-gray' },
  sent: { label: 'Sent', color: 'badge-blue' },
  in_progress: { label: 'In Progress', color: 'badge-yellow' },
  completed: { label: 'Completed', color: 'badge-green' },
  expired: { label: 'Expired', color: 'badge-red' },
}

const incomingStatusConfig: Record<string, { label: string; color: string }> = {
  uploaded: { label: 'Uploaded', color: 'badge-gray' },
  parsing: { label: 'Parsing...', color: 'badge-blue' },
  parsed: { label: 'Ready to Fill', color: 'badge-yellow' },
  answering: { label: 'AI Answering...', color: 'badge-blue' },
  reviewing: { label: 'Review Needed', color: 'badge-yellow' },
  completed: { label: 'Completed', color: 'badge-green' },
  sent: { label: 'Sent', color: 'badge-green' },
  failed: { label: 'Failed', color: 'badge-red' },
}

const assessmentRiskConfig: Record<string, { label: string; color: string }> = {
  critical: { label: 'Critical', color: 'badge-red' },
  high: { label: 'High', color: 'badge-yellow' },
  medium: { label: 'Medium', color: 'badge-blue' },
  low: { label: 'Low', color: 'badge-green' },
  minimal: { label: 'Minimal', color: 'badge-green' },
}

const categoryOptions = [
  'Cloud Provider',
  'SaaS',
  'Infrastructure',
  'Professional Services',
  'Data Processing',
  'Security',
  'Other',
]

// ============================================================================
// TPRM Methodology Modal
// ============================================================================

function TPRMMethodologyModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  if (!isOpen) return null

  const workflowSteps = [
    {
      num: 1,
      title: 'Add Vendors',
      desc: 'Build a centralized registry of every third party in your ecosystem — from SaaS providers to consultants.',
      icon: Building2,
      color: 'from-primary-600 to-primary-700',
      bg: 'bg-primary-50',
      border: 'border-primary-200',
      text: 'text-primary-700',
    },
    {
      num: 2,
      title: 'Assess Risk',
      desc: 'Evaluate each vendor across security, privacy, compliance, and operational dimensions with structured assessments.',
      icon: Shield,
      color: 'from-amber-500 to-amber-600',
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-700',
    },
    {
      num: 3,
      title: 'Send Questionnaires',
      desc: 'Generate AI-powered security questionnaires tailored to each vendor\'s risk profile and compliance frameworks.',
      icon: Send,
      color: 'from-sky-500 to-sky-600',
      bg: 'bg-sky-50',
      border: 'border-sky-200',
      text: 'text-sky-700',
    },
    {
      num: 4,
      title: 'Review Responses',
      desc: 'Track completion in real time, review vendor answers, and identify gaps in their security posture.',
      icon: Eye,
      color: 'from-emerald-500 to-emerald-600',
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      text: 'text-emerald-700',
    },
    {
      num: 5,
      title: 'Monitor & Reassess',
      desc: 'Schedule recurring assessments, set up automated reminders, and maintain continuous vendor oversight.',
      icon: RefreshCw,
      color: 'from-violet-500 to-violet-600',
      bg: 'bg-violet-50',
      border: 'border-violet-200',
      text: 'text-violet-700',
    },
  ]

  const tabs = [
    {
      icon: Building2,
      name: 'Vendors',
      desc: 'Central registry of all third-party vendors. Track risk levels, categories, contract dates, contacts, and domains. Each vendor is scored and classified to prioritize your due diligence efforts.',
      accent: 'bg-primary-100 text-primary-600',
    },
    {
      icon: Shield,
      name: 'Risk Assessments',
      desc: 'Structured risk evaluations per vendor covering security controls, data handling, compliance posture, and operational resilience. Assessments produce a quantified risk score.',
      accent: 'bg-amber-100 text-amber-600',
    },
    {
      icon: Send,
      name: 'Sent to Vendors',
      desc: 'Outgoing security questionnaires with AI-powered generation, email delivery with branded templates, real-time response tracking, and deadline management.',
      accent: 'bg-sky-100 text-sky-600',
    },
    {
      icon: Upload,
      name: 'Received from Others',
      desc: 'Incoming questionnaires from partners and clients. Upload received questionnaires, then use AI-assisted auto-fill to draft answers from your existing evidence library.',
      accent: 'bg-emerald-100 text-emerald-600',
    },
  ]

  const features = [
    { icon: BarChart3, label: 'Vendor Risk Scoring' },
    { icon: Mail, label: 'Automated Email Delivery' },
    { icon: Eye, label: 'Real-Time Response Tracking' },
    { icon: FileText, label: 'Template Library' },
    { icon: Target, label: 'Framework Mapping' },
    { icon: Calendar, label: 'Recurring Assessments' },
  ]

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-elevated w-full max-w-4xl max-h-[90vh] overflow-y-auto border border-gray-100">
        {/* Header */}
        <div className="sticky top-0 bg-white/95 backdrop-blur-sm flex items-center justify-between p-5 border-b border-gray-100 z-10 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl shadow-card">
              <BookOpen className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-heading font-semibold text-gray-900 tracking-snug">Third Party Risk Management</h2>
              <p className="text-sm text-gray-500">Understand and manage risk across your vendor ecosystem</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-10">

          {/* How It Works — Workflow */}
          <section>
            <h3 className="text-sm font-heading font-semibold text-gray-400 uppercase tracking-widest mb-5">How It Works</h3>
            <div className="relative">
              {/* Connecting line */}
              <div className="absolute left-[23px] top-10 bottom-10 w-px bg-gradient-to-b from-primary-300 via-sky-300 to-violet-300 hidden md:block" />

              <div className="space-y-4">
                {workflowSteps.map((step) => {
                  const Icon = step.icon
                  return (
                    <div key={step.num} className={`relative flex items-start gap-4 p-4 ${step.bg} rounded-xl border ${step.border} transition-all hover:shadow-card`}>
                      <div className={`flex-shrink-0 w-[46px] h-[46px] bg-gradient-to-br ${step.color} text-white rounded-xl flex items-center justify-center shadow-card relative z-10`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className={`text-xs font-semibold ${step.text} ring-1 ring-inset ring-current/20 rounded-full px-2 py-0.5`}>Step {step.num}</span>
                          <h4 className="font-heading font-semibold text-gray-900 tracking-snug">{step.title}</h4>
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </section>

          {/* Module Overview */}
          <section>
            <h3 className="text-sm font-heading font-semibold text-gray-400 uppercase tracking-widest mb-5">Module Overview</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {tabs.map((tab) => {
                const Icon = tab.icon
                return (
                  <div key={tab.name} className="bg-white border border-gray-100 rounded-xl p-5 shadow-card hover:shadow-card-hover transition-all">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${tab.accent}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <h4 className="font-heading font-semibold text-gray-900 tracking-snug">{tab.name}</h4>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">{tab.desc}</p>
                  </div>
                )
              })}
            </div>
          </section>

          {/* AI Features Callout */}
          <section>
            <div className="relative overflow-hidden rounded-xl border border-primary-200 bg-gradient-to-br from-primary-50 via-white to-sky-50">
              {/* Subtle decorative pattern */}
              <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'radial-gradient(circle at 20% 50%, currentColor 1px, transparent 1px)', backgroundSize: '24px 24px'}} />
              <div className="relative p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2.5 bg-gradient-to-br from-primary-600 to-sky-600 rounded-xl shadow-card">
                    <Sparkles className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 tracking-snug">AI-Powered Capabilities</h3>
                    <p className="text-xs text-gray-500">Built-in intelligence to accelerate your TPRM program</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3 p-3 bg-white/70 rounded-lg border border-primary-100">
                    <Brain className="h-5 w-5 text-primary-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Questionnaire Generation</p>
                      <p className="text-xs text-gray-600 mt-0.5">Auto-generate tailored questions based on vendor type, risk level, and compliance frameworks — SOC 2, ISO 27001, HIPAA, PCI DSS, NIST, GDPR, and CMMC.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-white/70 rounded-lg border border-primary-100">
                    <ClipboardList className="h-5 w-5 text-sky-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Auto-Fill Responses</p>
                      <p className="text-xs text-gray-600 mt-0.5">When you receive questionnaires from others, AI drafts answers from your existing evidence, policies, and prior responses — saving hours of manual work.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Key Features */}
          <section>
            <h3 className="text-sm font-heading font-semibold text-gray-400 uppercase tracking-widest mb-4">Key Features</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {features.map((f) => {
                const Icon = f.icon
                return (
                  <div key={f.label} className="flex items-center gap-2.5 p-3 bg-surface-50 rounded-lg border border-gray-100">
                    <Icon className="h-4 w-4 text-primary-600 flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-700">{f.label}</span>
                  </div>
                )
              })}
            </div>
          </section>

        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white/95 backdrop-blur-sm flex justify-end p-4 border-t border-gray-100 rounded-b-2xl">
          <button onClick={onClose} className="btn-secondary">Close</button>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Main Component
// ============================================================================

export default function TPRM() {
  const [activeTab, setActiveTab] = useState<'vendors' | 'assessments' | 'outgoing' | 'incoming'>('vendors')
  const [showMethodology, setShowMethodology] = useState(false)

  return (
    <div className="p-6">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Third Party Risk Management</h1>
          <p className="text-gray-500 mt-1">Manage vendors, assessments, and questionnaires</p>
        </div>
        <button
          onClick={() => setShowMethodology(true)}
          className="inline-flex items-center px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          title="How this works"
        >
          <HelpCircle className="h-4 w-4 mr-2" />
          How It Works
        </button>
      </div>

      <TPRMMethodologyModal isOpen={showMethodology} onClose={() => setShowMethodology(false)} />

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('vendors')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'vendors'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Building2 className="w-4 h-4 inline-block mr-2" />
            Vendors
          </button>
          <button
            onClick={() => setActiveTab('assessments')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'assessments'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Shield className="w-4 h-4 inline-block mr-2" />
            Risk Assessments
          </button>
          <button
            onClick={() => setActiveTab('outgoing')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'outgoing'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Send className="w-4 h-4 inline-block mr-2" />
            Sent to Vendors
          </button>
          <button
            onClick={() => setActiveTab('incoming')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'incoming'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Upload className="w-4 h-4 inline-block mr-2" />
            Received from Others
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'vendors' && <VendorsTab />}
      {activeTab === 'assessments' && <RiskAssessmentsTab />}
      {activeTab === 'outgoing' && <OutgoingQuestionnairesTab />}
      {activeTab === 'incoming' && <IncomingQuestionnairesTab />}
    </div>
  )
}

// ============================================================================
// Vendors Tab
// ============================================================================

function VendorsTab() {
  const [vendors, setVendors] = useState<Vendor[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [riskFilter, setRiskFilter] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    risk_level: 'medium',
    status: 'active',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    website: '',
    domains: [] as string[],
    notes: '',
  })
  const [newDomain, setNewDomain] = useState('')
  const [saving, setSaving] = useState(false)
  const toast = useToast()
  const confirm = useConfirm()

  useEffect(() => {
    fetchVendors()
  }, [search, statusFilter, riskFilter])

  async function fetchVendors() {
    setLoading(true)
    try {
      const res = await api.getVendors({
        search: search || undefined,
        status: statusFilter || undefined,
        risk_level: riskFilter || undefined,
      })
      if (res.success) {
        setVendors(res.vendors)
      }
    } catch (err) {
      console.error('Failed to load vendors:', err)
    } finally {
      setLoading(false)
    }
  }

  const openNewVendorModal = () => {
    setEditingVendor(null)
    setFormData({
      name: '',
      description: '',
      category: '',
      risk_level: 'medium',
      status: 'active',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      website: '',
      domains: [],
      notes: '',
    })
    setNewDomain('')
    setShowModal(true)
  }

  const openEditModal = (vendor: Vendor) => {
    setEditingVendor(vendor)
    // Parse domains from JSON string if needed
    let parsedDomains: string[] = []
    if (vendor.domains) {
      if (typeof vendor.domains === 'string') {
        try {
          parsedDomains = JSON.parse(vendor.domains)
        } catch {
          parsedDomains = []
        }
      } else {
        parsedDomains = vendor.domains
      }
    }
    setFormData({
      name: vendor.name,
      description: vendor.description || '',
      category: vendor.category || '',
      risk_level: vendor.risk_level,
      status: vendor.status,
      contact_name: vendor.contact_name || '',
      contact_email: vendor.contact_email || '',
      contact_phone: vendor.contact_phone || '',
      website: vendor.website || '',
      domains: parsedDomains,
      notes: vendor.notes || '',
    })
    setNewDomain('')
    setShowModal(true)
  }

  const addDomain = () => {
    const domain = newDomain.trim()
    if (domain && !formData.domains.includes(domain)) {
      setFormData({ ...formData, domains: [...formData.domains, domain] })
      setNewDomain('')
    }
  }

  const removeDomain = (domain: string) => {
    setFormData({ ...formData, domains: formData.domains.filter(d => d !== domain) })
  }

  async function handleSave() {
    if (!formData.name.trim()) {
      toast.warning('Vendor name is required')
      return
    }

    setSaving(true)
    try {
      if (editingVendor) {
        await api.updateVendor(editingVendor.id, formData)
        toast.success('Vendor updated successfully')
      } else {
        await api.createVendor(formData)
        toast.success('Vendor created successfully')
      }
      setShowModal(false)
      fetchVendors()
    } catch (err) {
      console.error('Failed to save vendor:', err)
      toast.error('Failed to save vendor')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(vendor: Vendor) {
    const confirmed = await confirm({
      title: 'Delete Vendor',
      message: `Delete vendor "${vendor.name}"? This action cannot be undone.`,
      type: 'danger',
      confirmText: 'Delete',
    })
    if (!confirmed) return

    try {
      await api.deleteVendor(vendor.id)
      toast.success('Vendor deleted successfully')
      fetchVendors()
    } catch (err) {
      console.error('Failed to delete vendor:', err)
      toast.error('Failed to delete vendor')
    }
  }

  return (
    <>
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-4 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search vendors..."
              className="input pl-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn-secondary ${showFilters ? 'bg-gray-100' : ''}`}
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
            <ChevronDown className="w-4 h-4 ml-2" />
          </button>
        </div>
        <button onClick={openNewVendorModal} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" />
          Add Vendor
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="card p-4 mb-4 flex gap-4">
          <div>
            <label className="label">Status</label>
            <select
              className="input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              {Object.entries(vendorStatusConfig).map(([value, { label }]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Risk Level</label>
            <select
              className="input"
              value={riskFilter}
              onChange={(e) => setRiskFilter(e.target.value)}
            >
              <option value="">All Risk Levels</option>
              {Object.entries(riskLevelConfig).map(([value, { label }]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Vendors List */}
      <div className="card">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading vendors...</div>
        ) : vendors.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No vendors found</p>
            <button onClick={openNewVendorModal} className="btn-primary mt-4">
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Vendor
            </button>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk Level</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Questionnaires</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {vendors.map((vendor) => (
                <tr key={vendor.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div>
                      <div className="font-medium text-gray-900">{vendor.name}</div>
                      {vendor.contact_email && (
                        <div className="text-sm text-gray-500">{vendor.contact_email}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{vendor.category || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={riskLevelConfig[vendor.risk_level]?.color || 'badge-gray'}>
                      {riskLevelConfig[vendor.risk_level]?.label || vendor.risk_level}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={vendorStatusConfig[vendor.status]?.color || 'badge-gray'}>
                      {vendorStatusConfig[vendor.status]?.label || vendor.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{vendor.questionnaire_count || 0}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => openEditModal(vendor)}
                      className="text-gray-400 hover:text-gray-600 p-1"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(vendor)}
                      className="text-gray-400 hover:text-red-600 p-1 ml-2"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Vendor Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">
                {editingVendor ? 'Edit Vendor' : 'Add Vendor'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="label">Vendor Name *</label>
                  <input
                    type="text"
                    className="input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <label className="label">Description</label>
                  <textarea
                    className="input"
                    rows={2}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>
                <div>
                  <label className="label">Category</label>
                  <select
                    className="input"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    <option value="">Select category</option>
                    {categoryOptions.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">Risk Level</label>
                  <select
                    className="input"
                    value={formData.risk_level}
                    onChange={(e) => setFormData({ ...formData, risk_level: e.target.value })}
                  >
                    {Object.entries(riskLevelConfig).map(([value, { label }]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">Status</label>
                  <select
                    className="input"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    {Object.entries(vendorStatusConfig).map(([value, { label }]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">Primary Website</label>
                  <input
                    type="url"
                    className="input"
                    placeholder="https://..."
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <label className="label">Additional Domains</label>
                  <p className="text-xs text-gray-500 mb-2">Add any other domains owned by this vendor (will be included in risk assessments)</p>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      className="input flex-1"
                      placeholder="example.com"
                      value={newDomain}
                      onChange={(e) => setNewDomain(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addDomain())}
                    />
                    <button
                      type="button"
                      onClick={addDomain}
                      className="btn-secondary px-4"
                    >
                      Add
                    </button>
                  </div>
                  {formData.domains.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.domains.map((domain) => (
                        <span
                          key={domain}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded"
                        >
                          {domain}
                          <button
                            type="button"
                            onClick={() => removeDomain(domain)}
                            className="text-gray-400 hover:text-red-500"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="label">Contact Name</label>
                  <input
                    type="text"
                    className="input"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="label">Contact Email</label>
                  <input
                    type="email"
                    className="input"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <label className="label">Notes</label>
                  <textarea
                    className="input"
                    rows={2}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t">
              <button onClick={() => setShowModal(false)} className="btn-secondary">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving} className="btn-primary">
                {saving ? 'Saving...' : editingVendor ? 'Update Vendor' : 'Add Vendor'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ============================================================================
// Outgoing Questionnaires Tab
// ============================================================================

interface QuestionnaireResponse {
  id: string
  question_number: number
  question: string
  response?: string
  answered_at?: number
}

function OutgoingQuestionnairesTab() {
  const { formatDate } = useDatePreferences()
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([])
  const [vendors, setVendors] = useState<Vendor[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [formData, setFormData] = useState({
    vendor_id: '',
    title: '',
    description: '',
    due_date: '',
    type: 'security' as string,
  })
  const [saving, setSaving] = useState(false)
  const toast = useToast()
  const confirmDialog = useConfirm()

  // Wizard state
  const [wizardStep, setWizardStep] = useState(1)
  const [questions, setQuestions] = useState<Array<{
    id: string
    category: string
    question: string
    question_type: string
    required: boolean
    help_text?: string
    choices?: string[]
    weight: number
  }>>([])
  const [questionSource, setQuestionSource] = useState<'ai' | 'template' | 'manual'>('ai')
  const [aiGenerating, setAiGenerating] = useState(false)
  const [aiFrameworks, setAiFrameworks] = useState<string[]>([])
  const [aiQuestionCount, setAiQuestionCount] = useState(15)
  const [templates, setTemplates] = useState<any[]>([])
  const [templatesLoaded, setTemplatesLoaded] = useState(false)
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<any>(null)
  const [showAddManual, setShowAddManual] = useState(false)
  const [manualForm, setManualForm] = useState({
    question: '',
    category: '',
    question_type: 'text',
    required: true,
    help_text: '',
    choices: [] as string[],
  })

  // Additional state for questionnaire actions
  const [openActionId, setOpenActionId] = useState<string | null>(null)
  const [showSendModal, setShowSendModal] = useState(false)
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<Questionnaire | null>(null)
  const [sendFormData, setSendFormData] = useState({
    vendor_email: '',
    vendor_contact_name: '',
    message: '',
  })
  const [sending, setSending] = useState(false)
  const [showResponsesModal, setShowResponsesModal] = useState(false)
  const [responses, setResponses] = useState<QuestionnaireResponse[]>([])
  const [loadingResponses, setLoadingResponses] = useState(false)

  useEffect(() => {
    fetchData()
  }, [statusFilter])

  async function fetchData() {
    setLoading(true)
    try {
      const [questRes, vendorRes] = await Promise.all([
        api.getQuestionnaires({ status: statusFilter || undefined }),
        api.getVendors(),
      ])
      if (questRes.success) setQuestionnaires(questRes.questionnaires)
      if (vendorRes.success) setVendors(vendorRes.vendors)
    } catch (err) {
      console.error('Failed to load data:', err)
    } finally {
      setLoading(false)
    }
  }

  function resetWizard() {
    setWizardStep(1)
    setFormData({ vendor_id: '', title: '', description: '', due_date: '', type: 'security' })
    setQuestions([])
    setQuestionSource('ai')
    setAiFrameworks([])
    setAiQuestionCount(15)
    setEditingQuestionId(null)
    setEditForm(null)
    setShowAddManual(false)
    setManualForm({ question: '', category: '', question_type: 'text', required: true, help_text: '', choices: [] })
  }

  async function handleCreate() {
    if (!formData.vendor_id || !formData.title) {
      toast.warning('Please select a vendor and enter a title')
      return
    }
    if (questions.length === 0) {
      toast.warning('Please add at least one question')
      return
    }

    setSaving(true)
    try {
      await api.createQuestionnaire({
        vendor_id: formData.vendor_id,
        title: formData.title,
        description: formData.description || undefined,
        due_date: formData.due_date ? new Date(formData.due_date).getTime() : undefined,
        questions: questions.map((q, i) => ({
          category: q.category,
          question: q.question,
          question_type: q.question_type,
          required: q.required,
          help_text: q.help_text,
          choices: q.choices,
          weight: q.weight,
          question_number: i + 1,
        })),
      })
      toast.success(`Questionnaire created with ${questions.length} questions`)
      setShowModal(false)
      resetWizard()
      fetchData()
    } catch (err) {
      console.error('Failed to create questionnaire:', err)
      toast.error('Failed to create questionnaire')
    } finally {
      setSaving(false)
    }
  }

  async function handleAIGenerate() {
    const selectedVendor = vendors.find(v => v.id === formData.vendor_id)
    setAiGenerating(true)
    try {
      const result = await api.generateQuestionnaireAI({
        vendor_type: selectedVendor?.category || undefined,
        risk_level: selectedVendor?.risk_level || undefined,
        frameworks: aiFrameworks.length > 0 ? aiFrameworks : undefined,
        question_count: aiQuestionCount,
      })
      if (result.success && result.questions) {
        const newQuestions = result.questions.map((q: any) => ({
          id: crypto.randomUUID(),
          category: q.category || 'General',
          question: q.question,
          question_type: q.question_type || 'text',
          required: q.required !== false,
          help_text: q.help_text || '',
          choices: q.choices || undefined,
          weight: q.weight || 1,
        }))
        setQuestions(newQuestions)
        toast.success(`Generated ${newQuestions.length} questions`)
      }
    } catch (err) {
      console.error('AI generation failed:', err)
      toast.error('Failed to generate questions. Please try again.')
    } finally {
      setAiGenerating(false)
    }
  }

  async function handleLoadTemplates() {
    if (templatesLoaded) return
    try {
      const result = await api.getQuestionnaireTemplates()
      if (result.success) {
        setTemplates(result.templates || [])
      }
    } catch (err) {
      console.error('Failed to load templates:', err)
    }
    setTemplatesLoaded(true)
  }

  function handleAddManualQuestion() {
    if (!manualForm.question.trim()) {
      toast.warning('Question text is required')
      return
    }
    setQuestions([...questions, {
      id: crypto.randomUUID(),
      category: manualForm.category || 'General',
      question: manualForm.question,
      question_type: manualForm.question_type,
      required: manualForm.required,
      help_text: manualForm.help_text || undefined,
      choices: manualForm.question_type === 'multiple-choice' && manualForm.choices.length > 0 ? manualForm.choices : undefined,
      weight: 1,
    }])
    setManualForm({ question: '', category: '', question_type: 'text', required: true, help_text: '', choices: [] })
    setShowAddManual(false)
  }

  function handleSaveEdit(id: string) {
    if (!editForm) return
    setQuestions(questions.map(q => q.id === id ? { ...q, ...editForm } : q))
    setEditingQuestionId(null)
    setEditForm(null)
  }

  function handleMoveQuestion(index: number, direction: 'up' | 'down') {
    const newQuestions = [...questions]
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    if (targetIndex < 0 || targetIndex >= newQuestions.length) return
    ;[newQuestions[index], newQuestions[targetIndex]] = [newQuestions[targetIndex], newQuestions[index]]
    setQuestions(newQuestions)
  }

  async function handleSaveAsTemplate() {
    if (questions.length === 0) return
    const name = prompt('Enter a name for this template:')
    if (!name) return
    try {
      await api.createQuestionnaireTemplate({
        name,
        description: formData.description || '',
        type: formData.type,
        vendor_tier: '',
        ai_generated: false,
        frameworks: aiFrameworks,
        questions: questions.map((q, i) => ({
          question_number: i + 1,
          category: q.category,
          question: q.question,
          question_type: q.question_type,
          required: q.required,
          help_text: q.help_text,
          choices: q.choices,
          weight: q.weight,
        })),
      })
      toast.success('Template saved')
      setTemplatesLoaded(false)
    } catch (err) {
      toast.error('Failed to save template')
    }
  }

  // Get link status badge
  function getLinkStatusBadge(q: Questionnaire) {
    if (!q.access_token) {
      return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">Not Sent</span>
    }
    const statusColors: Record<string, string> = {
      sent: 'bg-blue-100 text-blue-700',
      opened: 'bg-yellow-100 text-yellow-700',
      in_progress: 'bg-purple-100 text-purple-700',
      completed: 'bg-green-100 text-green-700',
      expired: 'bg-red-100 text-red-700',
    }
    const status = q.link_status || 'sent'
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${statusColors[status] || statusColors.sent}`}>
        {status.replace('_', ' ').replace(/^\w/, c => c.toUpperCase())}
        {q.link_views ? ` (${q.link_views} views)` : ''}
      </span>
    )
  }

  // Open send modal
  function handleOpenSendModal(q: Questionnaire) {
    setSelectedQuestionnaire(q)
    setSendFormData({
      vendor_email: q.vendor_email || '',
      vendor_contact_name: q.vendor_contact_name || '',
      message: '',
    })
    setShowSendModal(true)
  }

  // Copy questionnaire link
  async function handleCopyLink(q: Questionnaire) {
    if (q.access_token) {
      const url = `${window.location.origin}/questionnaire/${q.access_token}`
      await navigator.clipboard.writeText(url)
      toast.success('Link copied to clipboard')
    }
    setOpenActionId(null)
  }

  // Send reminder
  async function handleSendReminder(q: Questionnaire) {
    setOpenActionId(null)
    try {
      const result = await api.sendQuestionnaireReminder(q.id)
      if (result.success) {
        toast.success(`Reminder sent (${result.reminder_count} total reminders)`)
        fetchData()
      } else {
        toast.error('Failed to send reminder')
      }
    } catch (err) {
      toast.error('Failed to send reminder')
    }
  }

  // View responses
  async function handleViewResponses(q: Questionnaire) {
    setOpenActionId(null)
    setSelectedQuestionnaire(q)
    setShowResponsesModal(true)
    setLoadingResponses(true)
    try {
      const result = await api.getQuestionnaire(q.id)
      if (result.success && result.items) {
        setResponses(result.items as QuestionnaireResponse[])
      }
    } catch (err) {
      toast.error('Failed to load responses')
    } finally {
      setLoadingResponses(false)
    }
  }

  async function handleDeleteQuestionnaire(q: Questionnaire) {
    setOpenActionId(null)
    const confirmed = await confirmDialog({
      title: 'Delete Questionnaire',
      message: `Delete questionnaire "${q.title}"? This will also remove all questions and responses. This action cannot be undone.`,
      type: 'danger',
      confirmText: 'Delete',
    })
    if (!confirmed) return

    try {
      const res = await api.deleteQuestionnaire(q.id)
      if (res.success) {
        toast.success('Questionnaire deleted')
        fetchData()
      } else {
        toast.error('Failed to delete questionnaire')
      }
    } catch (err) {
      console.error('Failed to delete questionnaire:', err)
      toast.error('Failed to delete questionnaire')
    }
  }

  // Send questionnaire
  async function handleSendQuestionnaire() {
    if (!selectedQuestionnaire || !sendFormData.vendor_email) {
      toast.warning('Please enter the vendor email')
      return
    }

    setSending(true)
    try {
      const result = await api.sendQuestionnaire(selectedQuestionnaire.id, {
        vendor_email: sendFormData.vendor_email,
        vendor_contact_name: sendFormData.vendor_contact_name || undefined,
        message: sendFormData.message || undefined,
      })
      if (result.success) {
        toast.success('Questionnaire sent to vendor')
        setShowSendModal(false)
        setSelectedQuestionnaire(null)
        fetchData()
      } else {
        toast.error('Failed to send questionnaire')
      }
    } catch (err) {
      toast.error('Failed to send questionnaire')
    } finally {
      setSending(false)
    }
  }

  return (
    <>
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-4">
          <select
            className="input w-48"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            {Object.entries(questionnaireStatusConfig).map(([value, { label }]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <button onClick={() => setShowModal(true)} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" />
          Create Questionnaire
        </button>
      </div>

      {/* Questionnaires List */}
      <div className="card">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading questionnaires...</div>
        ) : questionnaires.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Send className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No questionnaires sent yet</p>
            <button onClick={() => setShowModal(true)} className="btn-primary mt-4">
              <Plus className="w-4 h-4 mr-2" />
              Create First Questionnaire
            </button>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Questionnaire</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Link Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {questionnaires.map((q) => (
                <tr key={q.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{q.title}</div>
                    {q.description && (
                      <div className="text-sm text-gray-500 truncate max-w-xs">{q.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{q.vendor_name || '-'}</td>
                  <td className="px-4 py-3">
                    {getLinkStatusBadge(q)}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {q.answered_count || 0} / {q.question_count || 0}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {q.due_date ? formatDate(q.due_date) : '-'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="relative inline-block">
                      <button
                        onClick={() => setOpenActionId(openActionId === q.id ? null : q.id)}
                        className="p-1 rounded hover:bg-gray-100"
                      >
                        <MoreVertical className="w-5 h-5 text-gray-500" />
                      </button>
                      {openActionId === q.id && (
                        <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border z-10">
                          {!q.access_token ? (
                            <button
                              onClick={() => {
                                setOpenActionId(null)
                                handleOpenSendModal(q)
                              }}
                              className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-gray-50 text-gray-700"
                            >
                              <Mail className="w-4 h-4" />
                              Send to Vendor
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={() => handleCopyLink(q)}
                                className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-gray-50 text-gray-700"
                              >
                                <Copy className="w-4 h-4" />
                                Copy Link
                              </button>
                              <button
                                onClick={() => handleSendReminder(q)}
                                className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-gray-50 text-gray-700"
                              >
                                <Bell className="w-4 h-4" />
                                Send Reminder
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleViewResponses(q)}
                            className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-gray-50 text-gray-700"
                          >
                            <Eye className="w-4 h-4" />
                            View Responses
                          </button>
                          <a
                            href={q.access_token ? `/questionnaire/${q.access_token}` : '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setOpenActionId(null)}
                            className={`w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-gray-50 ${
                              q.access_token ? 'text-gray-700' : 'text-gray-400 pointer-events-none'
                            }`}
                          >
                            <ExternalLink className="w-4 h-4" />
                            Open Form
                          </a>
                          <div className="border-t border-gray-100 my-1" />
                          <button
                            onClick={() => handleDeleteQuestionnaire(q)}
                            className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-red-50 text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Questionnaire Wizard */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-elevated w-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* Header with steps */}
            <div className="flex justify-between items-center p-4 border-b border-gray-100">
              <div className="flex items-center gap-4">
                <h2 className="text-lg font-heading font-semibold tracking-snug">Create Questionnaire</h2>
                <div className="flex items-center gap-1 text-sm">
                  {[
                    { n: 1, label: 'Details' },
                    { n: 2, label: 'Questions' },
                    { n: 3, label: 'Review' },
                  ].map((step, i) => (
                    <div key={step.n} className="flex items-center">
                      {i > 0 && <div className={`w-8 h-px mx-1 ${wizardStep >= step.n ? 'bg-primary-400' : 'bg-gray-200'}`} />}
                      <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        wizardStep === step.n ? 'bg-primary-100 text-primary-700' :
                        wizardStep > step.n ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-500'
                      }`}>
                        {wizardStep > step.n ? <CheckCircle className="w-3.5 h-3.5" /> : <span>{step.n}</span>}
                        {step.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <button onClick={() => { setShowModal(false); resetWizard() }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content area */}
            <div className="flex-1 overflow-y-auto p-5">
              {/* Step 1: Basic Info */}
              {wizardStep === 1 && (
                <div className="max-w-lg mx-auto space-y-4">
                  <div>
                    <label className="label">Vendor *</label>
                    <select className="input" value={formData.vendor_id} onChange={(e) => setFormData({ ...formData, vendor_id: e.target.value })}>
                      <option value="">Select a vendor</option>
                      {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label">Title *</label>
                    <input type="text" className="input" placeholder="e.g., Annual Security Assessment" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">Type</label>
                      <select className="input" value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                        <option value="security">Security</option>
                        <option value="privacy">Privacy</option>
                        <option value="compliance">Compliance</option>
                        <option value="financial">Financial</option>
                        <option value="operational">Operational</option>
                        <option value="custom">Custom</option>
                      </select>
                    </div>
                    <div>
                      <label className="label">Due Date</label>
                      <input type="date" className="input" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <label className="label">Description</label>
                    <textarea className="input" rows={2} placeholder="Optional description for the vendor..." value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
                  </div>
                </div>
              )}

              {/* Step 2: Questions */}
              {wizardStep === 2 && (
                <div className="space-y-5">
                  {/* Source tabs */}
                  <div className="flex gap-2 border-b border-gray-200 pb-0">
                    {([
                      { key: 'ai' as const, icon: Sparkles, label: 'AI Generate' },
                      { key: 'template' as const, icon: FileText, label: 'From Template' },
                      { key: 'manual' as const, icon: Plus, label: 'Add Manually' },
                    ]).map(tab => (
                      <button
                        key={tab.key}
                        onClick={() => {
                          setQuestionSource(tab.key)
                          if (tab.key === 'template') handleLoadTemplates()
                        }}
                        className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                          questionSource === tab.key
                            ? 'border-primary-500 text-primary-700'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <tab.icon className="w-4 h-4" />
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* AI Generate Panel */}
                  {questionSource === 'ai' && (
                    <div className="card p-4 space-y-4">
                      <p className="text-sm text-gray-600">Generate tailored questions using AI based on the vendor's profile and your compliance frameworks.</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="label">Vendor Type</label>
                          <input type="text" className="input" readOnly value={vendors.find(v => v.id === formData.vendor_id)?.category || 'General'} />
                        </div>
                        <div>
                          <label className="label">Risk Level</label>
                          <input type="text" className="input" readOnly value={vendors.find(v => v.id === formData.vendor_id)?.risk_level || 'Medium'} />
                        </div>
                      </div>
                      <div>
                        <label className="label">Frameworks</label>
                        <div className="flex flex-wrap gap-2">
                          {['SOC 2', 'ISO 27001', 'HIPAA', 'PCI DSS', 'NIST 800-53', 'GDPR', 'CMMC'].map(fw => (
                            <button
                              key={fw}
                              onClick={() => setAiFrameworks(prev => prev.includes(fw) ? prev.filter(f => f !== fw) : [...prev, fw])}
                              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                                aiFrameworks.includes(fw)
                                  ? 'bg-primary-50 border-primary-300 text-primary-700'
                                  : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                              }`}
                            >
                              {fw}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="label">Number of Questions: {aiQuestionCount}</label>
                        <input type="range" min={5} max={30} value={aiQuestionCount} onChange={(e) => setAiQuestionCount(Number(e.target.value))} className="w-full" />
                        <div className="flex justify-between text-xs text-gray-400"><span>5</span><span>30</span></div>
                      </div>
                      <button onClick={handleAIGenerate} disabled={aiGenerating} className="btn-primary w-full">
                        {aiGenerating ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating questions...</>
                        ) : (
                          <><Sparkles className="w-4 h-4 mr-2" />Generate {aiQuestionCount} Questions</>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Template Panel */}
                  {questionSource === 'template' && (
                    <div className="card p-4">
                      {templates.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <FileText className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                          <p className="text-sm">No templates saved yet</p>
                          <p className="text-xs mt-1">Create a questionnaire and save it as a template to reuse later.</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {templates.map((t: any) => (
                            <button
                              key={t.id}
                              onClick={async () => {
                                try {
                                  // Create from template, then fetch its questions
                                  const createResult = await api.createQuestionnaireFromTemplate(t.id, { vendor_id: formData.vendor_id })
                                  if (createResult.success) {
                                    const detail = await api.getQuestionnaire(createResult.questionnaire.id)
                                    if (detail.success && detail.items) {
                                      setQuestions(detail.items.map((q: any) => ({
                                        id: crypto.randomUUID(),
                                        category: q.category || 'General',
                                        question: q.question,
                                        question_type: q.question_type || 'text',
                                        required: q.required !== false,
                                        help_text: q.help_text,
                                        choices: q.choices ? (typeof q.choices === 'string' ? JSON.parse(q.choices) : q.choices) : undefined,
                                        weight: q.weight || 1,
                                      })))
                                      toast.success(`Loaded ${detail.items.length} questions from template`)
                                    }
                                  }
                                } catch (err) {
                                  toast.error('Failed to load template')
                                }
                              }}
                              className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50/30 transition-all text-left"
                            >
                              <div>
                                <p className="font-medium text-gray-900">{t.name}</p>
                                <p className="text-xs text-gray-500 mt-0.5">{t.question_count || 0} questions{t.type ? ` · ${t.type}` : ''}</p>
                              </div>
                              <ChevronDown className="w-4 h-4 text-gray-400 -rotate-90" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Manual Add Panel */}
                  {questionSource === 'manual' && (
                    <div className="card p-4">
                      {!showAddManual ? (
                        <button onClick={() => setShowAddManual(true)} className="w-full flex items-center justify-center gap-2 py-6 border-2 border-dashed border-gray-200 rounded-lg text-gray-500 hover:border-primary-300 hover:text-primary-600 transition-colors">
                          <Plus className="w-5 h-5" />
                          Add a question
                        </button>
                      ) : (
                        <div className="space-y-3">
                          <div>
                            <label className="label">Question *</label>
                            <textarea className="input" rows={2} placeholder="Enter your question..." value={manualForm.question} onChange={(e) => setManualForm({ ...manualForm, question: e.target.value })} />
                          </div>
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <label className="label">Category</label>
                              <input type="text" className="input" placeholder="e.g., Access Control" value={manualForm.category} onChange={(e) => setManualForm({ ...manualForm, category: e.target.value })} />
                            </div>
                            <div>
                              <label className="label">Type</label>
                              <select className="input" value={manualForm.question_type} onChange={(e) => setManualForm({ ...manualForm, question_type: e.target.value })}>
                                <option value="text">Text</option>
                                <option value="boolean">Yes / No</option>
                                <option value="rating">Rating (1-5)</option>
                                <option value="multiple-choice">Multiple Choice</option>
                              </select>
                            </div>
                            <div>
                              <label className="label">Required</label>
                              <select className="input" value={manualForm.required ? 'yes' : 'no'} onChange={(e) => setManualForm({ ...manualForm, required: e.target.value === 'yes' })}>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                              </select>
                            </div>
                          </div>
                          {manualForm.question_type === 'multiple-choice' && (
                            <div>
                              <label className="label">Choices</label>
                              <div className="space-y-1">
                                {manualForm.choices.map((c, i) => (
                                  <div key={i} className="flex items-center gap-2">
                                    <input type="text" className="input flex-1" value={c} onChange={(e) => {
                                      const newChoices = [...manualForm.choices]
                                      newChoices[i] = e.target.value
                                      setManualForm({ ...manualForm, choices: newChoices })
                                    }} />
                                    <button onClick={() => setManualForm({ ...manualForm, choices: manualForm.choices.filter((_, j) => j !== i) })} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                                  </div>
                                ))}
                                <button onClick={() => setManualForm({ ...manualForm, choices: [...manualForm.choices, ''] })} className="text-sm text-primary-600 hover:text-primary-700">+ Add choice</button>
                              </div>
                            </div>
                          )}
                          <div className="flex gap-2">
                            <button onClick={() => setShowAddManual(false)} className="btn-secondary text-sm">Cancel</button>
                            <button onClick={handleAddManualQuestion} className="btn-primary text-sm">Add Question</button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Question Editor - always visible when questions exist */}
                  {questions.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-medium text-gray-700">{questions.length} question{questions.length !== 1 ? 's' : ''}</h3>
                        <div className="flex items-center gap-2">
                          <button onClick={handleSaveAsTemplate} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-primary-600 transition-colors">
                            <Save className="w-3.5 h-3.5" />
                            Save as Template
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {questions.map((q, idx) => (
                          <div key={q.id} className="border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors">
                            {editingQuestionId === q.id ? (
                              /* Edit mode */
                              <div className="space-y-2">
                                <textarea className="input text-sm" rows={2} value={editForm?.question || ''} onChange={(e) => setEditForm({ ...editForm, question: e.target.value })} />
                                <div className="grid grid-cols-3 gap-2">
                                  <input type="text" className="input text-sm" placeholder="Category" value={editForm?.category || ''} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} />
                                  <select className="input text-sm" value={editForm?.question_type || 'text'} onChange={(e) => setEditForm({ ...editForm, question_type: e.target.value })}>
                                    <option value="text">Text</option>
                                    <option value="boolean">Yes / No</option>
                                    <option value="rating">Rating</option>
                                    <option value="multiple-choice">Multiple Choice</option>
                                  </select>
                                  <select className="input text-sm" value={editForm?.required ? 'yes' : 'no'} onChange={(e) => setEditForm({ ...editForm, required: e.target.value === 'yes' })}>
                                    <option value="yes">Required</option>
                                    <option value="no">Optional</option>
                                  </select>
                                </div>
                                <div className="flex gap-2">
                                  <button onClick={() => { setEditingQuestionId(null); setEditForm(null) }} className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                                  <button onClick={() => handleSaveEdit(q.id)} className="text-xs text-primary-600 hover:text-primary-700 font-medium">Save</button>
                                </div>
                              </div>
                            ) : (
                              /* View mode */
                              <div className="flex items-start gap-3">
                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-xs font-medium">{idx + 1}</span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-gray-900">{q.question}</p>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className="text-xs text-gray-500">{q.category}</span>
                                    <span className={`px-1.5 py-0.5 text-xs rounded ${
                                      q.question_type === 'boolean' ? 'bg-blue-50 text-blue-600' :
                                      q.question_type === 'rating' ? 'bg-purple-50 text-purple-600' :
                                      q.question_type === 'multiple-choice' ? 'bg-amber-50 text-amber-600' :
                                      'bg-gray-50 text-gray-600'
                                    }`}>{q.question_type}</span>
                                    {q.required && <span className="text-xs text-red-400">required</span>}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <button onClick={() => handleMoveQuestion(idx, 'up')} disabled={idx === 0} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
                                  <button onClick={() => handleMoveQuestion(idx, 'down')} disabled={idx === questions.length - 1} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
                                  <button onClick={() => { setEditingQuestionId(q.id); setEditForm({ ...q }) }} className="p-1 text-gray-400 hover:text-blue-600"><Edit2 className="w-3.5 h-3.5" /></button>
                                  <button onClick={() => setQuestions(questions.filter(x => x.id !== q.id))} className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      {/* Quick add at bottom */}
                      {questionSource !== 'manual' && (
                        <button
                          onClick={() => { setQuestionSource('manual'); setShowAddManual(true) }}
                          className="mt-2 w-full flex items-center justify-center gap-1.5 py-2 text-sm text-gray-500 hover:text-primary-600 border border-dashed border-gray-200 rounded-lg hover:border-primary-300 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          Add another question
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Review */}
              {wizardStep === 3 && (
                <div className="max-w-2xl mx-auto space-y-5">
                  <div className="card p-4">
                    <h3 className="font-medium text-gray-900 mb-3">Questionnaire Details</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-gray-500">Vendor:</span> <span className="font-medium">{vendors.find(v => v.id === formData.vendor_id)?.name}</span></div>
                      <div><span className="text-gray-500">Type:</span> <span className="font-medium capitalize">{formData.type}</span></div>
                      <div><span className="text-gray-500">Title:</span> <span className="font-medium">{formData.title}</span></div>
                      <div><span className="text-gray-500">Due Date:</span> <span className="font-medium">{formData.due_date || 'Not set'}</span></div>
                      <div className="col-span-2"><span className="text-gray-500">Questions:</span> <span className="font-medium">{questions.length}</span></div>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 mb-3">Questions Preview</h3>
                    <div className="space-y-2">
                      {questions.map((q, idx) => (
                        <div key={q.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-white text-gray-600 flex items-center justify-center text-xs font-medium border">{idx + 1}</span>
                          <div>
                            <p className="text-sm text-gray-900">{q.question}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-500">{q.category}</span>
                              <span className="text-xs text-gray-400">{q.question_type}</span>
                              {q.required && <span className="text-xs text-red-400">required</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-4 border-t border-gray-100">
              <div>
                {wizardStep > 1 && (
                  <button onClick={() => setWizardStep(wizardStep - 1)} className="btn-secondary text-sm">
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Back
                  </button>
                )}
              </div>
              <div className="flex gap-3">
                <button onClick={() => { setShowModal(false); resetWizard() }} className="btn-secondary text-sm">Cancel</button>
                {wizardStep === 1 && (
                  <button
                    onClick={() => {
                      if (!formData.vendor_id || !formData.title) {
                        toast.warning('Please select a vendor and enter a title')
                        return
                      }
                      setWizardStep(2)
                    }}
                    className="btn-primary text-sm"
                  >
                    Next: Add Questions
                  </button>
                )}
                {wizardStep === 2 && (
                  <button
                    onClick={() => {
                      if (questions.length === 0) {
                        toast.warning('Please add at least one question')
                        return
                      }
                      setWizardStep(3)
                    }}
                    className="btn-primary text-sm"
                  >
                    Review ({questions.length} questions)
                  </button>
                )}
                {wizardStep === 3 && (
                  <button onClick={handleCreate} disabled={saving} className="btn-primary text-sm">
                    {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Creating...</> : 'Create Questionnaire'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Questionnaire Modal */}
      {showSendModal && selectedQuestionnaire && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">Send Questionnaire to Vendor</h2>
              <button onClick={() => {
                setShowSendModal(false)
                setSelectedQuestionnaire(null)
              }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-medium text-gray-900">{selectedQuestionnaire.title}</p>
                <p className="text-sm text-gray-600">Vendor: {selectedQuestionnaire.vendor_name}</p>
              </div>
              <div>
                <label className="label">Vendor Email *</label>
                <input
                  type="email"
                  className="input"
                  placeholder="vendor@company.com"
                  value={sendFormData.vendor_email}
                  onChange={(e) => setSendFormData({ ...sendFormData, vendor_email: e.target.value })}
                />
              </div>
              <div>
                <label className="label">Contact Name</label>
                <input
                  type="text"
                  className="input"
                  placeholder="John Smith"
                  value={sendFormData.vendor_contact_name}
                  onChange={(e) => setSendFormData({ ...sendFormData, vendor_contact_name: e.target.value })}
                />
              </div>
              <div>
                <label className="label">Message (optional)</label>
                <textarea
                  className="input"
                  rows={3}
                  placeholder="Additional message for the vendor..."
                  value={sendFormData.message}
                  onChange={(e) => setSendFormData({ ...sendFormData, message: e.target.value })}
                />
              </div>
              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
                <p className="font-medium">What happens next:</p>
                <ul className="list-disc ml-4 mt-1 space-y-1">
                  <li>The vendor will receive an email with a unique link</li>
                  <li>They can complete the questionnaire without logging in</li>
                  <li>You can track progress and send reminders</li>
                </ul>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t">
              <button onClick={() => {
                setShowSendModal(false)
                setSelectedQuestionnaire(null)
              }} className="btn-secondary">Cancel</button>
              <button onClick={handleSendQuestionnaire} disabled={sending} className="btn-primary">
                {sending ? 'Sending...' : 'Send Questionnaire'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Responses Modal */}
      {showResponsesModal && selectedQuestionnaire && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <div>
                <h2 className="text-lg font-semibold">Questionnaire Responses</h2>
                <p className="text-sm text-gray-600">{selectedQuestionnaire.title} - {selectedQuestionnaire.vendor_name}</p>
              </div>
              <button onClick={() => {
                setShowResponsesModal(false)
                setSelectedQuestionnaire(null)
                setResponses([])
              }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {/* Summary info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 p-3 bg-gray-50 rounded-lg text-sm">
                {selectedQuestionnaire.vendor_email && (
                  <div>
                    <p className="text-gray-500 text-xs">Sent To</p>
                    <p className="font-medium text-gray-900">{selectedQuestionnaire.vendor_contact_name || selectedQuestionnaire.vendor_email}</p>
                    {selectedQuestionnaire.vendor_contact_name && (
                      <p className="text-gray-500 text-xs">{selectedQuestionnaire.vendor_email}</p>
                    )}
                  </div>
                )}
                {selectedQuestionnaire.sent_at && (
                  <div>
                    <p className="text-gray-500 text-xs">Sent</p>
                    <p className="font-medium text-gray-900">{formatDate(selectedQuestionnaire.sent_at)}</p>
                  </div>
                )}
                {selectedQuestionnaire.completed_at && (
                  <div>
                    <p className="text-gray-500 text-xs">Completed</p>
                    <p className="font-medium text-gray-900">{formatDate(selectedQuestionnaire.completed_at)}</p>
                  </div>
                )}
                <div>
                  <p className="text-gray-500 text-xs">Status</p>
                  <p className="font-medium text-gray-900 capitalize">{selectedQuestionnaire.status}</p>
                </div>
                {selectedQuestionnaire.link_views != null && selectedQuestionnaire.link_views > 0 && (
                  <div>
                    <p className="text-gray-500 text-xs">Link Views</p>
                    <p className="font-medium text-gray-900">{selectedQuestionnaire.link_views}</p>
                  </div>
                )}
                <div>
                  <p className="text-gray-500 text-xs">Progress</p>
                  <p className="font-medium text-gray-900">{selectedQuestionnaire.answered_count || 0} / {selectedQuestionnaire.question_count || 0} answered</p>
                </div>
              </div>

              {loadingResponses ? (
                <div className="text-center text-gray-500 py-8">Loading responses...</div>
              ) : responses.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No responses yet</p>
                  <p className="text-sm mt-1">The vendor has not submitted any answers.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {responses.map((r, idx) => (
                    <div key={r.id} className="border rounded-lg p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">
                            {r.question_number || idx + 1}. {r.question}
                          </p>
                          {r.response ? (
                            <p className="mt-2 text-gray-700 whitespace-pre-wrap">{r.response}</p>
                          ) : (
                            <p className="mt-2 text-gray-400 italic">No response</p>
                          )}
                        </div>
                        <span className={`shrink-0 ${
                          r.response ? 'badge-green' : 'badge-gray'
                        }`}>
                          {r.response ? 'Answered' : 'Pending'}
                        </span>
                      </div>
                      {r.answered_at && (
                        <p className="text-xs text-gray-500 mt-2">
                          Answered: {formatDate(r.answered_at)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 p-4 border-t">
              <button onClick={() => {
                setShowResponsesModal(false)
                setSelectedQuestionnaire(null)
                setResponses([])
              }} className="btn-secondary">Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ============================================================================
// Incoming Questionnaires Tab
// ============================================================================

function IncomingQuestionnairesTab() {
  const { formatDate } = useDatePreferences()
  const [questionnaires, setQuestionnaires] = useState<IncomingQuestionnaire[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('')
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<IncomingQuestionnaire | null>(null)
  const [questionnaireItems, setQuestionnaireItems] = useState<IncomingQuestionnaireItem[]>([])
  const [uploadData, setUploadData] = useState({
    file: null as File | null,
    sender_org: '',
    sender_contact: '',
    sender_email: '',
    title: '',
    due_date: '',
  })
  const [uploading, setUploading] = useState(false)
  const [processing, setProcessing] = useState(false)
  const toast = useToast()
  const confirmDialog = useConfirm()

  useEffect(() => {
    fetchQuestionnaires()
  }, [statusFilter])

  async function fetchQuestionnaires() {
    setLoading(true)
    try {
      const res = await api.getIncomingQuestionnaires({ status: statusFilter || undefined })
      if (res.success) setQuestionnaires(res.questionnaires)
    } catch (err) {
      console.error('Failed to load questionnaires:', err)
    } finally {
      setLoading(false)
    }
  }

  async function handleUpload() {
    if (!uploadData.file || !uploadData.sender_org) {
      toast.warning('Please select a file and enter the sender organization')
      return
    }

    setUploading(true)
    try {
      const res = await api.uploadIncomingQuestionnaire({
        file: uploadData.file,
        sender_org: uploadData.sender_org,
        sender_contact: uploadData.sender_contact || undefined,
        sender_email: uploadData.sender_email || undefined,
        title: uploadData.title || undefined,
        due_date: uploadData.due_date ? new Date(uploadData.due_date).getTime() : undefined,
      })
      if (res.success) {
        toast.success('Questionnaire uploaded successfully')
        setShowUploadModal(false)
        setUploadData({ file: null, sender_org: '', sender_contact: '', sender_email: '', title: '', due_date: '' })
        fetchQuestionnaires()
      } else {
        toast.error(res.error || 'Upload failed')
      }
    } catch (err) {
      console.error('Failed to upload:', err)
      toast.error('Failed to upload questionnaire')
    } finally {
      setUploading(false)
    }
  }

  async function handleParse(q: IncomingQuestionnaire) {
    setProcessing(true)
    try {
      const res = await api.parseIncomingQuestionnaire(q.id)
      if (res.success) {
        toast.success(`Extracted ${res.questions_count} questions`)
        fetchQuestionnaires()
      } else {
        toast.error('Failed to parse questionnaire')
      }
    } catch (err) {
      console.error('Failed to parse:', err)
      toast.error('Failed to parse questionnaire')
    } finally {
      setProcessing(false)
    }
  }

  async function handleAutofill(q: IncomingQuestionnaire) {
    const confirmed = await confirmDialog({
      title: 'Auto-fill Questionnaire',
      message: 'This will use AI to auto-fill answers based on your documents and evidence. Continue?',
      type: 'info',
      confirmText: 'Auto-fill',
    })
    if (!confirmed) return

    setProcessing(true)
    try {
      const res = await api.autofillIncomingQuestionnaire(q.id)
      if (res.success) {
        toast.success(`Auto-filled ${res.answered_count} of ${res.total_count} questions`)
        fetchQuestionnaires()
        if (selectedQuestionnaire?.id === q.id) {
          viewDetails(q)
        }
      } else {
        toast.error('Failed to auto-fill questionnaire')
      }
    } catch (err) {
      console.error('Failed to auto-fill:', err)
      toast.error('Failed to auto-fill questionnaire')
    } finally {
      setProcessing(false)
    }
  }

  async function viewDetails(q: IncomingQuestionnaire) {
    try {
      const res = await api.getIncomingQuestionnaire(q.id)
      if (res.success) {
        setSelectedQuestionnaire(res.questionnaire)
        setQuestionnaireItems(res.items)
        setShowDetailModal(true)
      }
    } catch (err) {
      console.error('Failed to load details:', err)
    }
  }

  async function handleApproveItem(item: IncomingQuestionnaireItem) {
    if (!selectedQuestionnaire) return

    try {
      await api.updateIncomingQuestionnaireItem(selectedQuestionnaire.id, item.id, {
        final_answer: item.ai_answer || '',
        status: 'approved',
      })
      // Refresh items
      viewDetails(selectedQuestionnaire)
    } catch (err) {
      console.error('Failed to approve item:', err)
    }
  }

  async function handleDelete(q: IncomingQuestionnaire) {
    const confirmed = await confirmDialog({
      title: 'Delete Questionnaire',
      message: `Delete questionnaire "${q.title || q.original_file_name}"? This action cannot be undone.`,
      type: 'danger',
      confirmText: 'Delete',
    })
    if (!confirmed) return

    try {
      const res = await api.deleteIncomingQuestionnaire(q.id)
      if (res.success) {
        toast.success('Questionnaire deleted successfully')
        fetchQuestionnaires()
        if (selectedQuestionnaire?.id === q.id) {
          setShowDetailModal(false)
          setSelectedQuestionnaire(null)
        }
      } else {
        toast.error('Failed to delete questionnaire')
      }
    } catch (err) {
      console.error('Failed to delete:', err)
      toast.error('Failed to delete questionnaire')
    }
  }

  async function handleExport(q: IncomingQuestionnaire) {
    try {
      const blob = await api.exportIncomingQuestionnaire(q.id)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${q.title || 'questionnaire'}_responses_${new Date().toISOString().split('T')[0]}.xlsx`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      toast.success('Export completed')
    } catch (err) {
      console.error('Failed to export:', err)
      toast.error('Failed to export questionnaire')
    }
  }

  return (
    <>
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-4">
          <select
            className="input w-48"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            {Object.entries(incomingStatusConfig).map(([value, { label }]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <button onClick={() => setShowUploadModal(true)} className="btn-primary">
          <Upload className="w-4 h-4 mr-2" />
          Upload Questionnaire
        </button>
      </div>

      {/* Questionnaires List */}
      <div className="card">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading questionnaires...</div>
        ) : questionnaires.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No incoming questionnaires</p>
            <p className="text-sm mt-2">Upload questionnaires received from third parties to auto-fill them using AI</p>
            <button onClick={() => setShowUploadModal(true)} className="btn-primary mt-4">
              <Upload className="w-4 h-4 mr-2" />
              Upload First Questionnaire
            </button>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Questionnaire</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">From</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {questionnaires.map((q) => (
                <tr key={q.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{q.title || q.original_file_name}</div>
                    {q.original_file_name && q.title && (
                      <div className="text-sm text-gray-500">{q.original_file_name}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-gray-900">{q.sender_org}</div>
                    {(q.sender_contact || q.sender_email) && (
                      <div className="text-sm text-gray-500">
                        {q.sender_contact}{q.sender_contact && q.sender_email ? ' • ' : ''}{q.sender_email}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={incomingStatusConfig[q.status]?.color || 'badge-gray'}>
                      {incomingStatusConfig[q.status]?.label || q.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {q.total_questions > 0 ? (
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-green-500 h-2 rounded-full"
                            style={{ width: `${(q.answered_questions / q.total_questions) * 100}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-600">
                          {q.answered_questions}/{q.total_questions}
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {q.due_date ? formatDate(q.due_date) : '-'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {q.status === 'uploaded' && (
                        <button
                          onClick={() => handleParse(q)}
                          disabled={processing}
                          className="btn-secondary text-xs py-1 px-2"
                          title="Extract questions from file"
                        >
                          <FileText className="w-3 h-3 mr-1" />
                          Parse
                        </button>
                      )}
                      {(q.status === 'parsed' || q.status === 'reviewing' || q.status === 'failed') && (
                        <button
                          onClick={() => handleAutofill(q)}
                          disabled={processing}
                          className="btn-primary text-xs py-1 px-2"
                          title="Auto-fill answers using AI"
                        >
                          <Sparkles className="w-3 h-3 mr-1" />
                          {q.status === 'failed' ? 'Retry' : 'Auto-Fill'}
                        </button>
                      )}
                      {q.total_questions > 0 && (
                        <>
                          <button
                            onClick={() => viewDetails(q)}
                            className="text-gray-400 hover:text-gray-600 p-1"
                            title="View details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleExport(q)}
                            className="text-gray-400 hover:text-green-600 p-1"
                            title="Download as Excel"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleDelete(q)}
                        className="text-gray-400 hover:text-red-600 p-1"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">Upload Questionnaire</h2>
              <button onClick={() => setShowUploadModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="label">File *</label>
                <input
                  type="file"
                  accept=".docx,.doc,.xlsx,.xls,.csv"
                  className="input"
                  onChange={(e) => setUploadData({ ...uploadData, file: e.target.files?.[0] || null })}
                />
                <p className="text-xs text-gray-500 mt-1">Supported: Word (.docx, .doc), Excel (.xlsx, .xls), CSV</p>
              </div>
              <div>
                <label className="label">Sender Organization *</label>
                <input
                  type="text"
                  className="input"
                  placeholder="e.g., Acme Corp"
                  value={uploadData.sender_org}
                  onChange={(e) => setUploadData({ ...uploadData, sender_org: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Contact Name</label>
                  <input
                    type="text"
                    className="input"
                    value={uploadData.sender_contact}
                    onChange={(e) => setUploadData({ ...uploadData, sender_contact: e.target.value })}
                  />
                </div>
                <div>
                  <label className="label">Contact Email</label>
                  <input
                    type="email"
                    className="input"
                    value={uploadData.sender_email}
                    onChange={(e) => setUploadData({ ...uploadData, sender_email: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="label">Title (optional)</label>
                <input
                  type="text"
                  className="input"
                  placeholder="Leave blank to use filename"
                  value={uploadData.title}
                  onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
                />
              </div>
              <div>
                <label className="label">Due Date</label>
                <input
                  type="date"
                  className="input"
                  value={uploadData.due_date}
                  onChange={(e) => setUploadData({ ...uploadData, due_date: e.target.value })}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t">
              <button onClick={() => setShowUploadModal(false)} className="btn-secondary">Cancel</button>
              <button onClick={handleUpload} disabled={uploading} className="btn-primary">
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedQuestionnaire && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <div>
                <h2 className="text-lg font-semibold">{selectedQuestionnaire.title || selectedQuestionnaire.original_file_name}</h2>
                <p className="text-sm text-gray-500">From: {selectedQuestionnaire.sender_org}</p>
              </div>
              <div className="flex items-center gap-2">
                {(selectedQuestionnaire.status === 'parsed' || selectedQuestionnaire.status === 'reviewing' || selectedQuestionnaire.status === 'failed') && (
                  <button
                    onClick={() => handleAutofill(selectedQuestionnaire)}
                    disabled={processing}
                    className="btn-primary text-sm"
                  >
                    <Sparkles className="w-4 h-4 mr-1" />
                    {processing ? 'Processing...' : 'Auto-Fill All'}
                  </button>
                )}
                <button
                  onClick={() => handleExport(selectedQuestionnaire)}
                  className="btn-secondary text-sm"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Export
                </button>
                <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {questionnaireItems.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No questions extracted yet</p>
              ) : (
                <div className="space-y-4">
                  {questionnaireItems.map((item) => (
                    <div key={item.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <span className="text-sm font-medium text-gray-500">Question {item.question_number}</span>
                          {item.category && (
                            <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                              {item.category}
                            </span>
                          )}
                        </div>
                        <span className={`text-xs ${
                          item.status === 'approved' ? 'badge-green' :
                          item.status === 'ai_answered' ? 'badge-blue' :
                          'badge-gray'
                        }`}>
                          {item.status === 'approved' ? 'Approved' :
                           item.status === 'ai_answered' ? 'AI Answered' :
                           'Pending'}
                        </span>
                      </div>
                      <p className="text-gray-900 font-medium mb-3">{item.question}</p>

                      {item.ai_answer && (
                        <div className="bg-blue-50 border border-blue-100 rounded p-3 mb-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Sparkles className="w-4 h-4 text-blue-500" />
                            <span className="text-sm font-medium text-blue-700">AI Suggested Answer</span>
                            {item.ai_confidence && (
                              <span className="text-xs text-blue-600">
                                ({Math.round(item.ai_confidence * 100)}% confidence)
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-700">{item.ai_answer}</p>
                          {item.ai_sources && (
                            <p className="text-xs text-gray-500 mt-2">
                              Sources: {JSON.parse(item.ai_sources).join(', ')}
                            </p>
                          )}
                        </div>
                      )}

                      {item.final_answer && (
                        <div className="bg-green-50 border border-green-100 rounded p-3 mb-3">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <span className="text-sm font-medium text-green-700">Final Answer</span>
                          </div>
                          <p className="text-sm text-gray-700">{item.final_answer}</p>
                        </div>
                      )}

                      {item.status === 'ai_answered' && !item.final_answer && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleApproveItem(item)}
                            className="btn-primary text-xs py-1 px-3"
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Approve AI Answer
                          </button>
                          <button className="btn-secondary text-xs py-1 px-3">
                            <Edit2 className="w-3 h-3 mr-1" />
                            Edit
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ============================================================================
// Risk Assessments Tab
// ============================================================================

function RiskAssessmentsTab() {
  const { formatDate } = useDatePreferences()
  const [assessments, setAssessments] = useState<RiskAssessment[]>([])
  const [selfAssessments, setSelfAssessments] = useState<RiskAssessment[]>([])
  const [queue, setQueue] = useState<AssessmentQueueItem[]>([])
  const [vendors, setVendors] = useState<Vendor[]>([])
  const [loading, setLoading] = useState(true)
  const [subTab, setSubTab] = useState<'assessments' | 'self' | 'queue'>('assessments')
  const [showQueueModal, setShowQueueModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [selectedAssessment, setSelectedAssessment] = useState<RiskAssessment | null>(null)
  const [queueForm, setQueueForm] = useState({ vendor_id: '', priority: 5, reason: '' })
  const [running, setRunning] = useState(false)
  const [runningSelf, setRunningSelf] = useState(false)
  const [completedAssessment, setCompletedAssessment] = useState<any>(null)
  const [showRunningModal, setShowRunningModal] = useState(false)
  const [assessmentPhase, setAssessmentPhase] = useState<'confirm' | 'running' | 'complete' | 'error'>('confirm')
  const [assessmentVendor, setAssessmentVendor] = useState<{ id: string; name: string } | null>(null)
  const [assessmentError, setAssessmentError] = useState<string | null>(null)
  const toast = useToast()
  const confirmDialog = useConfirm()

  useEffect(() => {
    fetchData()
  }, [subTab])

  async function fetchData() {
    setLoading(true)
    try {
      const vendorRes = await api.getVendors()
      if (vendorRes.success) setVendors(vendorRes.vendors)

      if (subTab === 'assessments') {
        const res = await api.getRiskAssessments()
        if (res.success) setAssessments(res.assessments)
      } else if (subTab === 'self') {
        const res = await api.getSelfAssessmentHistory()
        if (res.success) setSelfAssessments(res.assessments)
      } else if (subTab === 'queue') {
        // Only fetch active queue items (queued or processing, not completed/cancelled)
        const res = await api.getAssessmentQueue({ status: 'queued' })
        if (res.success) setQueue(res.queue)
      }
    } catch (err) {
      console.error('Failed to load data:', err)
    } finally {
      setLoading(false)
    }
  }

  async function handleQueueVendor() {
    if (!queueForm.vendor_id) {
      toast.warning('Please select a vendor')
      return
    }

    try {
      const res = await api.queueAssessment({
        vendor_id: queueForm.vendor_id,
        priority: queueForm.priority,
        reason: queueForm.reason || 'Manual assessment request',
      })
      if (res.success) {
        toast.success('Assessment queued successfully')
        setShowQueueModal(false)
        setQueueForm({ vendor_id: '', priority: 5, reason: '' })
        fetchData()
      } else {
        toast.error(res.error || 'Failed to queue assessment')
      }
    } catch (err) {
      console.error('Failed to queue:', err)
      toast.error('Failed to queue assessment')
    }
  }

  // Opens the confirmation modal
  function handleRunAssessment(vendorId: string, vendorName: string) {
    setAssessmentVendor({ id: vendorId, name: vendorName })
    setAssessmentPhase('confirm')
    setCompletedAssessment(null)
    setAssessmentError(null)
    setShowRunningModal(true)
  }

  // Actually runs the assessment after user confirms
  async function executeAssessment() {
    if (!assessmentVendor) return

    setAssessmentPhase('running')
    setRunning(true)

    try {
      const res = await api.runRiskAssessment(assessmentVendor.id)
      if (res.success) {
        setCompletedAssessment(res.assessment)
        setAssessmentPhase('complete')
        fetchData()
      } else {
        setAssessmentError(res.error || 'Assessment failed')
        setAssessmentPhase('error')
      }
    } catch (err) {
      console.error('Assessment failed:', err)
      setAssessmentError('Failed to run assessment. Please try again.')
      setAssessmentPhase('error')
    } finally {
      setRunning(false)
    }
  }

  function handleCloseRunningModal() {
    setShowRunningModal(false)
    setCompletedAssessment(null)
    setAssessmentVendor(null)
    setAssessmentError(null)
    if (assessmentPhase === 'complete') {
      setSubTab('assessments')
    }
  }

  function handleViewAssessmentFromModal() {
    if (completedAssessment) {
      setSelectedAssessment(completedAssessment)
      setShowRunningModal(false)
      setCompletedAssessment(null)
      setAssessmentVendor(null)
      setShowDetailModal(true)
    }
  }

  async function viewAssessmentDetails(assessment: RiskAssessment) {
    try {
      const res = await api.getRiskAssessment(assessment.id)
      if (res.success) {
        setSelectedAssessment(res.assessment)
        setShowDetailModal(true)
      }
    } catch (err) {
      console.error('Failed to load details:', err)
    }
  }

  async function handleRunSelfAssessment() {
    const confirmed = await confirmDialog({
      title: 'Run Self-Assessment',
      message: 'Run a risk assessment on your own organization? This will analyze your website\'s security posture using public data sources.',
      type: 'info',
      confirmText: 'Run Assessment',
    })
    if (!confirmed) return

    setRunningSelf(true)
    try {
      const res = await api.runSelfAssessment()
      if (res.success) {
        toast.success(`Self-Assessment completed! Risk Level: ${res.assessment.overall_risk_level}, Score: ${res.assessment.overall_risk_score}/100`)
        setSubTab('self')
        fetchData()
      } else {
        toast.error(res.error || 'Self-assessment failed')
      }
    } catch (err) {
      console.error('Self-assessment failed:', err)
      toast.error('Failed to run self-assessment. Please ensure your organization website is configured in Settings.')
    } finally {
      setRunningSelf(false)
    }
  }

  return (
    <>
      {/* Sub-tab Navigation */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setSubTab('assessments')}
          className={`px-4 py-2 text-sm font-medium rounded-lg ${
            subTab === 'assessments'
              ? 'bg-primary-100 text-primary-700'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Shield className="w-4 h-4 inline-block mr-2" />
          Vendor Assessments
        </button>
        <button
          onClick={() => setSubTab('self')}
          className={`px-4 py-2 text-sm font-medium rounded-lg ${
            subTab === 'self'
              ? 'bg-primary-100 text-primary-700'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Globe className="w-4 h-4 inline-block mr-2" />
          Self-Assessments
        </button>
        <button
          onClick={() => setSubTab('queue')}
          className={`px-4 py-2 text-sm font-medium rounded-lg ${
            subTab === 'queue'
              ? 'bg-primary-100 text-primary-700'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Clock className="w-4 h-4 inline-block mr-2" />
          Assessment Queue
        </button>
      </div>

      {/* Action Header */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-500">
            {subTab === 'assessments' && `${assessments.length} vendor assessments`}
            {subTab === 'self' && `${selfAssessments.length} self-assessments`}
            {subTab === 'queue' && `${queue.filter(q => q.status === 'queued').length} vendors in queue`}
          </div>
        </div>
        <div className="flex gap-2">
          {subTab === 'self' && (
            <button
              onClick={handleRunSelfAssessment}
              disabled={runningSelf}
              className="btn-primary flex items-center"
            >
              {runningSelf ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Run Self-Assessment
                </>
              )}
            </button>
          )}
          {subTab === 'queue' && (
            <button onClick={() => setShowQueueModal(true)} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Queue Vendor
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="card">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : subTab === 'assessments' ? (
          assessments.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Shield className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No risk assessments completed yet</p>
              <p className="text-sm mt-2">Queue vendors for assessment or run assessments from the Vendors tab</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk Level</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scores Breakdown</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {assessments.map((a) => (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{a.vendor_name}</div>
                      {a.vendor_website && (
                        <div className="text-sm text-gray-500">{a.vendor_website}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={assessmentRiskConfig[a.overall_risk_level || '']?.color || 'badge-gray'}>
                        {assessmentRiskConfig[a.overall_risk_level || '']?.label || a.overall_risk_level}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-lg font-semibold">{a.overall_risk_score}</span>
                      <span className="text-gray-400">/100</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-3 text-xs">
                        <div title="Security">
                          <span className="text-gray-500">SEC:</span> {a.security_score}
                        </div>
                        <div title="Reputation">
                          <span className="text-gray-500">REP:</span> {a.reputation_score}
                        </div>
                        <div title="Compliance">
                          <span className="text-gray-500">COMP:</span> {a.compliance_score}
                        </div>
                        <div title="Dark Web Exposure">
                          <span className="text-gray-500">DW:</span> {a.dark_web_score}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-sm">
                      {a.completed_at ? formatDate(a.completed_at) : '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => viewAssessmentDetails(a)}
                        className="text-gray-400 hover:text-gray-600 p-1"
                        title="View details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : subTab === 'self' ? (
          selfAssessments.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Globe className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No self-assessments completed yet</p>
              <p className="text-sm mt-2">Run a self-assessment to analyze your organization's security posture</p>
              <button
                onClick={handleRunSelfAssessment}
                disabled={runningSelf}
                className="btn-primary mt-4"
              >
                {runningSelf ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Running...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Run First Self-Assessment
                  </>
                )}
              </button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organization</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk Level</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scores Breakdown</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {selfAssessments.map((a) => (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{a.vendor_name || 'Your Organization'}</div>
                      {a.vendor_website && (
                        <div className="text-sm text-gray-500">{a.vendor_website}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={assessmentRiskConfig[a.overall_risk_level || '']?.color || 'badge-gray'}>
                        {assessmentRiskConfig[a.overall_risk_level || '']?.label || a.overall_risk_level}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-lg font-semibold">{a.overall_risk_score}</span>
                      <span className="text-gray-400">/100</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-3 text-xs">
                        <div title="Security">
                          <span className="text-gray-500">SEC:</span> {a.security_score}
                        </div>
                        <div title="Reputation">
                          <span className="text-gray-500">REP:</span> {a.reputation_score}
                        </div>
                        <div title="Compliance">
                          <span className="text-gray-500">COMP:</span> {a.compliance_score}
                        </div>
                        <div title="Dark Web Exposure">
                          <span className="text-gray-500">DW:</span> {a.dark_web_score}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-sm">
                      {a.completed_at ? formatDate(a.completed_at) : '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => viewAssessmentDetails(a)}
                        className="text-gray-400 hover:text-gray-600 p-1"
                        title="View details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : (
          queue.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No vendors in assessment queue</p>
              <button onClick={() => setShowQueueModal(true)} className="btn-primary mt-4">
                <Plus className="w-4 h-4 mr-2" />
                Queue First Vendor
              </button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {queue.map((q) => (
                  <tr key={q.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{q.vendor_name}</div>
                      {q.vendor_website && (
                        <div className="text-sm text-gray-500">{q.vendor_website}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`badge-${q.priority <= 3 ? 'red' : q.priority <= 6 ? 'yellow' : 'gray'}`}>
                        P{q.priority}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-sm">{q.reason || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`badge-${q.status === 'queued' ? 'blue' : q.status === 'processing' ? 'yellow' : 'green'}`}>
                        {q.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {q.status === 'queued' && (
                        <button
                          onClick={() => handleRunAssessment(q.vendor_id, q.vendor_name || 'Vendor')}
                          disabled={running}
                          className="btn-primary text-xs py-1 px-2"
                          title="Run now"
                        >
                          <Play className="w-3 h-3 mr-1" />
                          Run
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>

      {/* Queue Modal */}
      {showQueueModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">Queue Vendor for Assessment</h2>
              <button onClick={() => setShowQueueModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="label">Vendor *</label>
                <select
                  className="input"
                  value={queueForm.vendor_id}
                  onChange={(e) => setQueueForm({ ...queueForm, vendor_id: e.target.value })}
                >
                  <option value="">Select a vendor</option>
                  {vendors.filter(v => v.website).map((v) => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Only vendors with a website can be assessed</p>
              </div>
              <div>
                <label className="label">Priority (1=highest, 10=lowest)</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  className="input"
                  value={queueForm.priority}
                  onChange={(e) => setQueueForm({ ...queueForm, priority: parseInt(e.target.value) || 5 })}
                />
              </div>
              <div>
                <label className="label">Reason</label>
                <input
                  type="text"
                  className="input"
                  placeholder="e.g., Annual review, New vendor onboarding"
                  value={queueForm.reason}
                  onChange={(e) => setQueueForm({ ...queueForm, reason: e.target.value })}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t">
              <button onClick={() => setShowQueueModal(false)} className="btn-secondary">Cancel</button>
              <button onClick={handleQueueVendor} className="btn-primary">Queue Assessment</button>
            </div>
          </div>
        </div>
      )}

      {/* Running Assessment Modal */}
      {showRunningModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            {/* Confirm Phase */}
            {assessmentPhase === 'confirm' && assessmentVendor && (
              <div className="text-center">
                <div className="mb-4">
                  <Shield className="w-12 h-12 mx-auto text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Run Risk Assessment</h3>
                <p className="text-gray-600 mb-2">
                  <strong>{assessmentVendor.name}</strong>
                </p>
                <p className="text-gray-500 text-sm mb-6">
                  This will analyze public data sources and dark web intelligence to assess the vendor's security posture.
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={handleCloseRunningModal}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg border"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={executeAssessment}
                    className="btn-primary"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Start Assessment
                  </button>
                </div>
              </div>
            )}

            {/* Running Phase */}
            {assessmentPhase === 'running' && (
              <div className="text-center">
                <div className="mb-6">
                  <div className="w-16 h-16 mx-auto border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Running Assessment</h3>
                <p className="text-gray-600 mb-1">
                  <strong>{assessmentVendor?.name}</strong>
                </p>
                <p className="text-gray-500 text-sm mb-4">
                  Analyzing public data sources and dark web intelligence...
                </p>
                <div className="text-xs text-gray-400 space-y-1">
                  <p>Checking ransomware databases...</p>
                  <p>Analyzing DNS security records...</p>
                  <p>Scanning security headers...</p>
                  <p>Monitoring dark web sources...</p>
                </div>
              </div>
            )}

            {/* Complete Phase */}
            {assessmentPhase === 'complete' && completedAssessment && (
              <div className="text-center">
                <div className="mb-4">
                  <CheckCircle className="w-12 h-12 mx-auto text-green-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Assessment Complete</h3>

                {/* Score Circle */}
                <div className="relative w-32 h-32 mx-auto mb-4">
                  <svg className="w-32 h-32 transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="#e5e7eb"
                      strokeWidth="12"
                      fill="none"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke={
                        completedAssessment.overall_risk_score >= 75 ? '#22c55e' :
                        completedAssessment.overall_risk_score >= 50 ? '#eab308' :
                        completedAssessment.overall_risk_score >= 25 ? '#f97316' : '#ef4444'
                      }
                      strokeWidth="12"
                      fill="none"
                      strokeDasharray={`${(completedAssessment.overall_risk_score / 100) * 352} 352`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div>
                      <div className="text-3xl font-bold">{completedAssessment.overall_risk_score}</div>
                      <div className="text-xs text-gray-500">Score</div>
                    </div>
                  </div>
                </div>

                {/* Risk Level Badge */}
                <div className="mb-6">
                  <span className={`text-lg font-semibold px-4 py-2 rounded-full ${
                    completedAssessment.overall_risk_level === 'minimal' ? 'bg-green-100 text-green-800' :
                    completedAssessment.overall_risk_level === 'low' ? 'bg-blue-100 text-blue-800' :
                    completedAssessment.overall_risk_level === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    completedAssessment.overall_risk_level === 'high' ? 'bg-orange-100 text-orange-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {completedAssessment.overall_risk_level?.charAt(0).toUpperCase() + completedAssessment.overall_risk_level?.slice(1)} Risk
                  </span>
                </div>

                <p className="text-sm text-gray-600 mb-6">
                  {completedAssessment.vendor_name} has been assessed
                </p>

                {/* Action Buttons */}
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={handleCloseRunningModal}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    Close
                  </button>
                  <button
                    onClick={handleViewAssessmentFromModal}
                    className="btn-primary"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View Details
                  </button>
                </div>
              </div>
            )}

            {/* Error Phase */}
            {assessmentPhase === 'error' && (
              <div className="text-center">
                <div className="mb-4">
                  <AlertTriangle className="w-12 h-12 mx-auto text-red-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Assessment Failed</h3>
                <p className="text-red-600 text-sm mb-6">
                  {assessmentError || 'An unexpected error occurred'}
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={handleCloseRunningModal}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg border"
                  >
                    Close
                  </button>
                  <button
                    onClick={executeAssessment}
                    className="btn-primary"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Retry
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Assessment Detail Modal */}
      {showDetailModal && selectedAssessment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white">
              <div>
                <h2 className="text-lg font-semibold">{selectedAssessment.vendor_name}</h2>
                <p className="text-sm text-gray-500">Risk Assessment Details</p>
              </div>
              <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              {/* Risk Summary */}
              <div className="grid grid-cols-5 gap-4 mb-6">
                <div className="text-center p-4 bg-gray-50 rounded-lg col-span-1">
                  <div className="text-3xl font-bold">{selectedAssessment.overall_risk_score}</div>
                  <div className="text-xs text-gray-500">Overall Score</div>
                  <span className={`mt-2 inline-block ${assessmentRiskConfig[selectedAssessment.overall_risk_level || '']?.color || 'badge-gray'}`}>
                    {assessmentRiskConfig[selectedAssessment.overall_risk_level || '']?.label || selectedAssessment.overall_risk_level}
                  </span>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-semibold text-blue-700">{selectedAssessment.security_score}</div>
                  <div className="text-xs text-blue-600">Security</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-semibold text-green-700">{selectedAssessment.reputation_score}</div>
                  <div className="text-xs text-green-600">Reputation</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-semibold text-purple-700">{selectedAssessment.compliance_score}</div>
                  <div className="text-xs text-purple-600">Compliance</div>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <div className="text-2xl font-semibold text-red-700">{selectedAssessment.dark_web_score}</div>
                  <div className="text-xs text-red-600">Dark Web Exposure</div>
                </div>
              </div>

              {/* AI Summary */}
              {selectedAssessment.ai_summary && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">AI Analysis Summary</h3>
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                    <p className="text-sm text-gray-700">{selectedAssessment.ai_summary}</p>
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {selectedAssessment.ai_recommendations && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Recommendations</h3>
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                    {JSON.parse(selectedAssessment.ai_recommendations).map((rec: string, idx: number) => (
                      <li key={idx}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Detailed Data Sources */}
              {selectedAssessment.raw_source_data && (() => {
                try {
                  const rawData = JSON.parse(selectedAssessment.raw_source_data)
                  return (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-gray-700 mb-3">Detailed Assessment Data</h3>
                      <div className="space-y-3">
                        {rawData.map((source: any, idx: number) => {
                          // Calculate item count for each source type
                          const getItemCount = () => {
                            if (!source.data || !source.success) return null
                            if (source.source === 'ransomware.live' || source.source.startsWith('ransomware_live')) {
                              return source.data.matchCount || source.data.victims?.length || 0
                            }
                            if (source.source.startsWith('dns_analysis')) {
                              let count = 0
                              if (source.data.hasSPF) count++
                              if (source.data.hasDMARC) count++
                              const missing = 2 - count
                              return missing > 0 ? `${missing} missing` : 'All configured'
                            }
                            if (source.source.startsWith('security_headers')) {
                              return `${source.data.score || 0}/100`
                            }
                            if (source.source === 'ssl_check' || source.source.startsWith('ssl_tls')) {
                              return source.data.isValid ? 'Valid' : 'Invalid'
                            }
                            if (source.source.startsWith('psbdmp')) {
                              return source.data.matchCount || source.data.dumps?.length || 0
                            }
                            if (source.source.startsWith('github_exposure')) {
                              return source.data.totalResults || source.data.sampleFiles?.length || 0
                            }
                            if (source.source.startsWith('telegram_search')) {
                              return source.data.totalMentions || source.data.channels?.length || 0
                            }
                            return null
                          }
                          const itemCount = getItemCount()
                          const isNumericCount = typeof itemCount === 'number'
                          const hasFindings = isNumericCount && itemCount > 0

                          return (
                          <details key={idx} className="border rounded-lg">
                            <summary className="px-4 py-3 cursor-pointer bg-gray-50 hover:bg-gray-100 rounded-t-lg font-medium text-sm flex items-center justify-between">
                              <span className="flex items-center gap-2">
                                {(source.source === 'ransomware.live' || source.source.startsWith('ransomware_live')) && '🔐'}
                                {source.source.startsWith('dns_analysis') && '📧'}
                                {source.source.startsWith('security_headers') && '🛡️'}
                                {(source.source === 'ssl_check' || source.source.startsWith('ssl_tls')) && '🔒'}
                                {source.source.startsWith('psbdmp') && '📋'}
                                {source.source.startsWith('github_exposure') && '💻'}
                                {source.source.startsWith('telegram_search') && '📱'}
                                {source.source.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                              </span>
                              <span className="flex items-center gap-2">
                                {itemCount !== null && (
                                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                                    hasFindings ? 'bg-orange-100 text-orange-700' :
                                    typeof itemCount === 'string' && itemCount.includes('missing') ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-gray-100 text-gray-600'
                                  }`}>
                                    {isNumericCount ? `${itemCount} found` : itemCount}
                                  </span>
                                )}
                                <span className={`text-xs px-2 py-0.5 rounded ${source.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                  {source.success ? 'Success' : 'Failed'}
                                </span>
                              </span>
                            </summary>
                            <div className="px-4 py-3 text-sm border-t bg-white">
                              {source.error && (
                                <p className="text-red-600 mb-2">Error: {source.error}</p>
                              )}
                              {source.data && (source.source === 'ransomware.live' || source.source.startsWith('ransomware_live')) && (
                                <div>
                                  <p className="text-gray-600 mb-2">
                                    <strong>Search Terms:</strong> {source.data.searchTerms?.join(', ') || 'N/A'}
                                  </p>
                                  <p className="text-gray-600 mb-2">
                                    <strong>Incidents Found:</strong> {source.data.matchCount || 0}
                                  </p>
                                  {source.data.victims && source.data.victims.length > 0 && (
                                    <div className="mt-2">
                                      <strong className="text-gray-700">Ransomware Incidents:</strong>
                                      <ul className="mt-1 space-y-1 ml-4">
                                        {source.data.victims.map((v: any, i: number) => (
                                          <li key={i} className="text-red-600 text-xs">
                                            {v.group} attacked {v.victim} ({v.domain}) on {v.attackdate?.split(' ')[0]}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              )}
                              {source.data && source.source.startsWith('dns_analysis') && (
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2">
                                    <span className={source.data.hasSPF ? 'text-green-600' : 'text-red-600'}>
                                      {source.data.hasSPF ? '✓' : '✗'}
                                    </span>
                                    <span>SPF Record</span>
                                    {source.data.spfRecord && (
                                      <code className="text-xs bg-gray-100 px-1 py-0.5 rounded ml-2 max-w-md truncate block">
                                        {source.data.spfRecord}
                                      </code>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className={source.data.hasDMARC ? 'text-green-600' : 'text-red-600'}>
                                      {source.data.hasDMARC ? '✓' : '✗'}
                                    </span>
                                    <span>DMARC Record</span>
                                    {source.data.dmarcRecord && (
                                      <code className="text-xs bg-gray-100 px-1 py-0.5 rounded ml-2 max-w-md truncate block">
                                        {source.data.dmarcRecord}
                                      </code>
                                    )}
                                  </div>
                                  {source.data.mxRecords && source.data.mxRecords.length > 0 && (
                                    <div>
                                      <strong className="text-gray-700 text-xs">MX Records:</strong>
                                      <ul className="text-xs text-gray-600 ml-4">
                                        {source.data.mxRecords.slice(0, 5).map((mx: string, i: number) => (
                                          <li key={i}>{mx}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              )}
                              {source.data && source.source.startsWith('security_headers') && (
                                <div className="space-y-2">
                                  <p className="text-gray-600">
                                    <strong>Header Score:</strong> {source.data.score}/100
                                  </p>
                                  <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div className={`flex items-center gap-1 ${source.data.hasHSTS ? 'text-green-600' : 'text-red-600'}`}>
                                      {source.data.hasHSTS ? '✓' : '✗'} HSTS
                                    </div>
                                    <div className={`flex items-center gap-1 ${source.data.hasCSP ? 'text-green-600' : 'text-red-600'}`}>
                                      {source.data.hasCSP ? '✓' : '✗'} Content-Security-Policy
                                    </div>
                                    <div className={`flex items-center gap-1 ${source.data.hasXFrameOptions ? 'text-green-600' : 'text-red-600'}`}>
                                      {source.data.hasXFrameOptions ? '✓' : '✗'} X-Frame-Options
                                    </div>
                                    <div className={`flex items-center gap-1 ${source.data.hasXContentTypeOptions ? 'text-green-600' : 'text-red-600'}`}>
                                      {source.data.hasXContentTypeOptions ? '✓' : '✗'} X-Content-Type-Options
                                    </div>
                                    <div className={`flex items-center gap-1 ${source.data.hasReferrerPolicy ? 'text-green-600' : 'text-red-600'}`}>
                                      {source.data.hasReferrerPolicy ? '✓' : '✗'} Referrer-Policy
                                    </div>
                                  </div>
                                  {source.data.missing && source.data.missing.length > 0 && (
                                    <p className="text-xs text-orange-600">
                                      Missing: {source.data.missing.join(', ')}
                                    </p>
                                  )}
                                </div>
                              )}
                              {source.data && (source.source === 'ssl_check' || source.source.startsWith('ssl_tls')) && (
                                <div>
                                  <p className={source.data.isValid ? 'text-green-600' : 'text-red-600'}>
                                    SSL/TLS: {source.data.isValid ? 'Valid' : 'Invalid or Unavailable'}
                                  </p>
                                  {source.data.protocol && (
                                    <p className="text-gray-600 text-xs">Protocol: {source.data.protocol}</p>
                                  )}
                                </div>
                              )}
                              {source.data && source.source.startsWith('psbdmp') && (
                                <div className="space-y-2">
                                  <p className="text-gray-600">
                                    <strong>Search Term:</strong> {source.data.searchTerm}
                                  </p>
                                  <p className={source.data.matchCount > 0 ? 'text-orange-600 font-medium' : 'text-green-600'}>
                                    <strong>Pastebin Dumps Found:</strong> {source.data.matchCount || 0}
                                  </p>
                                  {source.data.dumps && source.data.dumps.length > 0 && (
                                    <div className="mt-2">
                                      <strong className="text-gray-700 text-xs">Pastebin Links:</strong>
                                      <ul className="text-xs ml-4 space-y-1">
                                        {source.data.dumps.slice(0, 5).map((dump: any, i: number) => {
                                          const dumpId = typeof dump === 'string' ? dump : dump.id
                                          return (
                                            <li key={i}>
                                              <a
                                                href={`https://pastebin.com/${dumpId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:text-blue-800 hover:underline"
                                              >
                                                pastebin.com/{dumpId}
                                              </a>
                                            </li>
                                          )
                                        })}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              )}
                              {source.data && source.source.startsWith('github_exposure') && (
                                <div className="space-y-2">
                                  <p className="text-gray-600">
                                    <strong>Searched Domain:</strong> {source.data.searchedDomain}
                                  </p>
                                  <p className={source.data.totalResults > 0 ? 'text-orange-600 font-medium' : 'text-green-600'}>
                                    <strong>Code Results Found:</strong> {source.data.totalResults || 0}
                                  </p>
                                  {source.data.sampleFiles && source.data.sampleFiles.length > 0 && (
                                    <div className="mt-2">
                                      <strong className="text-gray-700 text-xs">Sample Files:</strong>
                                      <ul className="text-xs text-gray-600 ml-4 space-y-1">
                                        {source.data.sampleFiles.slice(0, 5).map((file: any, i: number) => (
                                          <li key={i}>
                                            <a href={file.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                              {file.repo}/{file.path}
                                            </a>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              )}
                              {source.data && source.source.startsWith('telegram_search') && (
                                <div className="space-y-2">
                                  <p className="text-gray-600">
                                    <strong>Search Terms:</strong> {source.data.searchTerms?.join(', ') || 'N/A'}
                                  </p>
                                  {source.data.channels && source.data.channels.length > 0 ? (
                                    <div className="mt-2">
                                      <p className="text-gray-600 mb-2">
                                        <strong>Channels Found:</strong> {source.data.channels.length}
                                        {source.data.totalMentions > 0 && ` (${source.data.totalMentions} total mentions)`}
                                      </p>
                                      <ul className="text-xs text-gray-600 ml-4 space-y-1">
                                        {source.data.channels.slice(0, 5).map((channel: any, i: number) => (
                                          <li key={i}>
                                            <a href={channel.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                              @{channel.handle}
                                            </a>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  ) : (
                                    <p className="text-gray-500 italic">No mentions found in public Telegram channels</p>
                                  )}
                                </div>
                              )}
                              {!source.data && !source.error && (
                                <p className="text-gray-500 italic">No additional data available</p>
                              )}
                            </div>
                          </details>
                          )
                        })}
                      </div>
                    </div>
                  )
                } catch {
                  return null
                }
              })()}

              {/* Data Sources */}
              {selectedAssessment.data_sources && (
                <div className="text-xs text-gray-500">
                  <span className="font-medium">Data sources used:</span> {JSON.parse(selectedAssessment.data_sources).join(', ')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </>
  )
}
